import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { handleAxiosError } from '@/shared';
import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants';
import { AlertSeverityEnum } from '@/shared/interfaces';
import {
  ITicketEstado,
  ITicketPrioridad,
  ITicketArea,
  ITicketCanal,
  ITicketTipo,
  ITicketCategoria,
  ITicketSubcategoria,
} from '@/shared/interfaces/erp/tickets-soporte-clientes';
import { useUiStore } from '@/store/ui';

import {
  USE_MOCK,
  MOCK_ESTADOS,
  MOCK_PRIORIDADES,
  MOCK_AREAS,
  MOCK_CANALES,
  MOCK_TIPOS,
  MOCK_CATEGORIAS,
  MOCK_SUBCATEGORIAS,
  mockAxiosResponse,
} from './tickets-mock-data';

const { get, post, put, patch, remove } = erpApi();

const BASE_URL = `${urlBackend}api/v1/gestion-tickets/`;

const FIVE_MINUTES = 1000 * 60 * 5;

///* tanStack query
type CatalogFetchParams = {
  page_size?: number;
  enabled?: boolean;
};

export const useFetchTicketEstados = (params?: CatalogFetchParams) => {
  const pageSize = params?.page_size ?? 100;
  return useQuery({
    queryKey: ['ticket-estados', pageSize],
    queryFn: async () => {
      if (USE_MOCK) return mockAxiosResponse({ data: MOCK_ESTADOS, count: MOCK_ESTADOS.length });
      return get<ITicketEstado[]>(`${BASE_URL}catalogo/estados/?page_size=${pageSize}`, true);
    },
    staleTime: FIVE_MINUTES,
    enabled: params?.enabled !== false,
  });
};

export const useFetchTicketPrioridades = (params?: CatalogFetchParams) => {
  const pageSize = params?.page_size ?? 100;
  return useQuery({
    queryKey: ['ticket-prioridades', pageSize],
    queryFn: async () => {
      if (USE_MOCK) return mockAxiosResponse({ data: MOCK_PRIORIDADES, count: MOCK_PRIORIDADES.length });
      return get<ITicketPrioridad[]>(`${BASE_URL}catalogo/prioridades/?page_size=${pageSize}`, true);
    },
    staleTime: FIVE_MINUTES,
    enabled: params?.enabled !== false,
  });
};

export const useFetchTicketAreas = (params?: CatalogFetchParams) => {
  const pageSize = params?.page_size ?? 100;
  return useQuery({
    queryKey: ['ticket-areas', pageSize],
    queryFn: async () => {
      if (USE_MOCK) return mockAxiosResponse({ data: MOCK_AREAS, count: MOCK_AREAS.length });
      return get<ITicketArea[]>(`${BASE_URL}catalogo/areas/?page_size=${pageSize}`, true);
    },
    staleTime: FIVE_MINUTES,
    enabled: params?.enabled !== false,
  });
};

export const useFetchTicketCanales = () => {
  return useQuery({
    queryKey: ['ticket-canales'],
    queryFn: async () => {
      if (USE_MOCK) return mockAxiosResponse({ data: MOCK_CANALES, count: MOCK_CANALES.length });
      return get<ITicketCanal[]>(`${BASE_URL}catalogo/canales/?page_size=100`, true);
    },
    staleTime: FIVE_MINUTES,
  });
};

/**
 * Area del usuario logueado. Resuelta por el backend via UsuarioArea
 * segun el header X-User-Id. Util para mostrar read-only "Tu area"
 * en los forms de crear ticket/ticket interno.
 */
export const useFetchMyTicketArea = () => {
  return useQuery({
    queryKey: ['ticket-my-area'],
    queryFn: async () => {
      if (USE_MOCK) return mockAxiosResponse({ id_area: '', nombre: 'Mi Area', codigo: '' });
      return get<{ id_area: string; nombre: string; codigo: string }>(
        `${BASE_URL}catalogo/me/area`,
        true,
      );
    },
    staleTime: FIVE_MINUTES,
  });
};

export interface UseFetchTicketTiposParams {
  paraMasivos?: boolean;
  /**
   * UUID del area por la que filtrar. Si se envia, el backend devuelve los
   * tipos de esa area + los legacy (area NULL). Si no se envia, devuelve
   * todos (caso pantalla admin "todas las areas").
   */
  areaId?: string | null;
  enabled?: boolean;
}

export const useFetchTicketTipos = (
  paramsOrParaMasivos?: UseFetchTicketTiposParams | boolean,
) => {
  // Backwards-compat: el llamado historico era `useFetchTicketTipos(paraMasivos: boolean)`.
  const params: UseFetchTicketTiposParams =
    typeof paramsOrParaMasivos === 'boolean'
      ? { paraMasivos: paramsOrParaMasivos }
      : (paramsOrParaMasivos || {});
  const { paraMasivos, areaId, enabled = true } = params;

  return useQuery({
    queryKey: ['ticket-tipos', paraMasivos ? 'masivos' : 'all', areaId || 'all-areas'],
    queryFn: async () => {
      if (USE_MOCK) return mockAxiosResponse({ data: MOCK_TIPOS, count: MOCK_TIPOS.length });
      const parts: string[] = ['page_size=100'];
      if (paraMasivos) parts.push('para_masivos=true');
      if (areaId) parts.push(`area_id=${areaId}`);
      return get<ITicketTipo[]>(`${BASE_URL}catalogo/tipos/?${parts.join('&')}`, true);
    },
    staleTime: FIVE_MINUTES,
    enabled,
  });
};

/**
 * GET /catalogo/areas-permitidas — devuelve las areas donde el usuario actual
 * tiene es_responsable=true en api2_usuario_area. Para admins globales el
 * frontend en su lugar puede usar useFetchTicketAreas (devuelve todas).
 */
export const useFetchAreasPermitidas = () => {
  return useQuery({
    queryKey: ['areas-permitidas'],
    queryFn: async () => {
      return get<ITicketArea[]>(`${BASE_URL}catalogo/areas-permitidas/`, true);
    },
    staleTime: FIVE_MINUTES,
  });
};

export const useFetchTicketCategorias = (
  tipoId?: string,
  paraMasivos?: boolean,
  areaId?: string,
) => {
  return useQuery({
    queryKey: ['ticket-categorias', tipoId, paraMasivos ? 'masivos' : 'all', areaId || 'all-areas'],
    queryFn: async () => {
      if (USE_MOCK) {
        const filtered = tipoId ? MOCK_CATEGORIAS.filter((c) => c.tipo === tipoId) : MOCK_CATEGORIAS;
        return mockAxiosResponse({ data: filtered, count: filtered.length });
      }
      const parts: string[] = ['page_size=100'];
      if (tipoId) parts.push(`tipo=${tipoId}`);
      if (areaId) parts.push(`area_id=${areaId}`);
      if (paraMasivos) parts.push('para_masivos=true');
      return get<ITicketCategoria[]>(`${BASE_URL}catalogo/categorias/?${parts.join('&')}`, true);
    },
    staleTime: FIVE_MINUTES,
  });
};

/**
 * GET /catalogo/motivos-cierre?area_id=... — Lista los motivos de cierre
 * configurados para un area. Si no se pasa areaId, devuelve todos (admin).
 *
 * Endpoint dedicado que filtra subcategorias bajo categoria.codigo='motivos_cierre'
 * por area y respeta el flag api2_area.incluir_tipos_legacy.
 */
export const useFetchMotivosCierre = (areaId?: string | null, enabled = true) => {
  return useQuery({
    queryKey: ['motivos-cierre', areaId || 'all-areas'],
    queryFn: async () => {
      const parts: string[] = [];
      if (areaId) parts.push(`area_id=${areaId}`);
      const qs = parts.length > 0 ? `?${parts.join('&')}` : '';
      return get<ITicketSubcategoria[]>(`${BASE_URL}catalogo/motivos-cierre${qs}`, true);
    },
    staleTime: FIVE_MINUTES,
    enabled,
  });
};

// =============================================================================
// ARBOL DE CIERRE (V1_19_0) — Categoria + Subcategoria por area
// Reemplaza el modelo viejo de 1 dropdown plano.
// =============================================================================

export interface ICierreSubcategoria {
  id_cierre_subcategoria: string;
  codigo: string;
  nombre: string;
  activa?: boolean;
}

export interface ICierreCategoriaArbol {
  id_cierre_categoria: string;
  codigo: string;
  nombre: string;
  activa?: boolean;
  es_generica: boolean;
  area_id: string | null;
  area_nombre: string | null;
  subcategorias: ICierreSubcategoria[];
}

/**
 * GET /catalogo/cierre/arbol?area_id=... -> arbol completo (categorias +
 * subcategorias) que debe mostrarse al cerrar un ticket de esa area.
 * Smart fallback: si el area no tiene propias, devuelve genericas.
 */
export const useFetchArbolCierre = (areaId?: string | null, enabled = true) => {
  return useQuery({
    queryKey: ['arbol-cierre', areaId || 'all-areas'],
    queryFn: async () => {
      const qs = areaId ? `?area_id=${areaId}` : '';
      return get<ICierreCategoriaArbol[]>(`${BASE_URL}catalogo/cierre/arbol${qs}`, true);
    },
    staleTime: FIVE_MINUTES,
    enabled,
  });
};

// ---- CRUD Categoria de Cierre ----

export const useCreateCierreCategoria = () => {
  const queryClient = useQueryClient();
  const setAlert = useUiStore.getState().setAlert;
  return useMutation({
    mutationFn: async (data: { codigo: string; nombre: string; area?: string | null; activa?: boolean }) => {
      return post(`${BASE_URL}catalogo/cierre/categorias/`, data, true);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['arbol-cierre'] });
      setAlert?.(true, 'Categoria de cierre creada', AlertSeverityEnum.success);
    },
    onError: (error) => handleAxiosError(error),
  });
};

export const useUpdateCierreCategoria = () => {
  const queryClient = useQueryClient();
  const setAlert = useUiStore.getState().setAlert;
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      return put(`${BASE_URL}catalogo/cierre/categorias/${id}/`, data, true);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['arbol-cierre'] });
      setAlert?.(true, 'Categoria actualizada', AlertSeverityEnum.success);
    },
    onError: (error) => handleAxiosError(error),
  });
};

export const useDeleteCierreCategoria = () => {
  const queryClient = useQueryClient();
  const setAlert = useUiStore.getState().setAlert;
  return useMutation({
    mutationFn: async (id: string) => {
      return remove(`${BASE_URL}catalogo/cierre/categorias/${id}/`, true);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['arbol-cierre'] });
      setAlert?.(true, 'Categoria eliminada', AlertSeverityEnum.success);
    },
    onError: (error) => handleAxiosError(error),
  });
};

// ---- CRUD Subcategoria de Cierre ----

export const useCreateCierreSubcategoria = () => {
  const queryClient = useQueryClient();
  const setAlert = useUiStore.getState().setAlert;
  return useMutation({
    mutationFn: async (data: { codigo: string; nombre: string; cierre_categoria: string; activa?: boolean }) => {
      return post(`${BASE_URL}catalogo/cierre/subcategorias/`, data, true);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['arbol-cierre'] });
      setAlert?.(true, 'Subcategoria de cierre creada', AlertSeverityEnum.success);
    },
    onError: (error) => handleAxiosError(error),
  });
};

export const useUpdateCierreSubcategoria = () => {
  const queryClient = useQueryClient();
  const setAlert = useUiStore.getState().setAlert;
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      return put(`${BASE_URL}catalogo/cierre/subcategorias/${id}/`, data, true);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['arbol-cierre'] });
      setAlert?.(true, 'Subcategoria actualizada', AlertSeverityEnum.success);
    },
    onError: (error) => handleAxiosError(error),
  });
};

export const useDeleteCierreSubcategoria = () => {
  const queryClient = useQueryClient();
  const setAlert = useUiStore.getState().setAlert;
  return useMutation({
    mutationFn: async (id: string) => {
      return remove(`${BASE_URL}catalogo/cierre/subcategorias/${id}/`, true);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['arbol-cierre'] });
      setAlert?.(true, 'Subcategoria eliminada', AlertSeverityEnum.success);
    },
    onError: (error) => handleAxiosError(error),
  });
};

export const useFetchTicketSubcategorias = (
  categoriaId?: string,
  paraMasivos?: boolean,
  areaId?: string,
) => {
  return useQuery({
    queryKey: ['ticket-subcategorias', categoriaId, paraMasivos ? 'masivos' : 'all', areaId || 'all-areas'],
    queryFn: async () => {
      if (USE_MOCK) {
        const filtered = categoriaId ? MOCK_SUBCATEGORIAS.filter((s) => s.categoria === categoriaId) : MOCK_SUBCATEGORIAS;
        return mockAxiosResponse({ data: filtered, count: filtered.length });
      }
      const parts: string[] = ['page_size=100'];
      if (categoriaId) parts.push(`categoria=${categoriaId}`);
      if (areaId) parts.push(`area_id=${areaId}`);
      if (paraMasivos) parts.push('para_masivos=true');
      return get<ITicketSubcategoria[]>(`${BASE_URL}catalogo/subcategorias/?${parts.join('&')}`, true);
    },
    staleTime: FIVE_MINUTES,
  });
};

// =============================================================================
// CRUD Mutations genéricas para catálogos
// =============================================================================

type CatalogType = 'estados' | 'prioridades' | 'areas' | 'canales' | 'tipos' | 'categorias' | 'subcategorias';

const CATALOG_QUERY_KEYS: Record<CatalogType, string> = {
  estados: 'ticket-estados',
  prioridades: 'ticket-prioridades',
  areas: 'ticket-areas',
  canales: 'ticket-canales',
  tipos: 'ticket-tipos',
  categorias: 'ticket-categorias',
  subcategorias: 'ticket-subcategorias',
};

/**
 * Invalida TODAS las queries relacionadas con un catalogType. Importante:
 * los motivos de cierre son subcategorias bajo la categoria especial
 * 'motivos_cierre'. Si el caller crea/edita/borra una subcategoria, hay
 * que invalidar tambien la queryKey ['motivos-cierre', ...] que usa el
 * PanelMotivosCierre para refrescar inmediatamente la UI.
 */
const invalidateForCatalog = (queryClient: ReturnType<typeof useQueryClient>, catalogType: CatalogType) => {
  queryClient.invalidateQueries({ queryKey: [CATALOG_QUERY_KEYS[catalogType]] });
  if (catalogType === 'subcategorias') {
    queryClient.invalidateQueries({ queryKey: ['motivos-cierre'] });
  }
  // El flag "Aplica para cierre" de las categorias (sus subcategorias
  // activas y el area del tipo) alimenta el arbol de cierre: cualquier
  // cambio en estos 3 catalogos refresca tambien ese arbol para que el
  // tab "Arbol de Cierre" y el modal de cerrar lo vean al instante.
  if (catalogType === 'tipos' || catalogType === 'categorias' || catalogType === 'subcategorias') {
    queryClient.invalidateQueries({ queryKey: ['arbol-cierre'] });
  }
};

export const useCreateCatalogItem = (catalogType: CatalogType) => {
  const queryClient = useQueryClient();
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: async (data: any) => {
      return post(`${BASE_URL}catalogo/${catalogType}/`, data, true);
    },
    onSuccess: () => {
      invalidateForCatalog(queryClient, catalogType);
      setAlert?.(true, 'Elemento creado correctamente', AlertSeverityEnum.success);
    },
    onError: (error) => handleAxiosError(error),
  });
};

export const useUpdateCatalogItem = (catalogType: CatalogType) => {
  const queryClient = useQueryClient();
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      return put(`${BASE_URL}catalogo/${catalogType}/${id}/`, data, true);
    },
    onSuccess: () => {
      invalidateForCatalog(queryClient, catalogType);
      setAlert?.(true, 'Elemento actualizado correctamente', AlertSeverityEnum.success);
    },
    onError: (error) => handleAxiosError(error),
  });
};

export const useDeleteCatalogItem = (catalogType: CatalogType) => {
  const queryClient = useQueryClient();
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: async (id: string) => {
      return remove(`${BASE_URL}catalogo/${catalogType}/${id}/`, true);
    },
    onSuccess: () => {
      invalidateForCatalog(queryClient, catalogType);
      setAlert?.(true, 'Elemento eliminado correctamente', AlertSeverityEnum.success);
    },
    onError: (error) => handleAxiosError(error),
  });
};

/**
 * Dispara POST /catalogo/areas/sincronizar-erp/ en tickets-service.
 * Lee api_departamento de base_erp (read-only) y hace UPSERT en api2_area.
 * Devuelve un resumen con los conteos { total_erp, nuevos, actualizados,
 * sin_cambios, total_areas }. Tras completar, invalida la query de areas
 * para que la tabla se refresque sin recarga manual.
 */
export const useSincronizarAreasDesdeErp = () => {
  const queryClient = useQueryClient();
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: async () => {
      return post<Record<string, number>>(
        `${BASE_URL}catalogo/areas/sincronizar-erp/`,
        {},
        true,
      );
    },
    onSuccess: (res: any) => {
      queryClient.invalidateQueries({ queryKey: ['ticket-areas'] });
      const d = res?.data?.data ?? res?.data ?? {};
      const msg = `Sincronizacion completa. Nuevos: ${d.nuevos ?? 0}, actualizados: ${d.actualizados ?? 0}, sin cambios: ${d.sin_cambios ?? 0}.`;
      setAlert?.(true, msg, AlertSeverityEnum.success);
    },
    onError: (error) => handleAxiosError(error),
  });
};

/**
 * Dispara PATCH /catalogo/areas/{id}/incluir-legacy con {incluir: bool}.
 * Solo el admin global (permiso catalogos.toggle_legacy_por_area) puede
 * disparar este endpoint. Tras toggle invalida la query de areas para
 * que la tabla del panel refleje el nuevo valor sin recarga manual.
 */
export const useToggleAreaIncluirLegacy = () => {
  const queryClient = useQueryClient();
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: async ({ id, incluir }: { id: string; incluir: boolean }) => {
      return patch<ITicketArea>(
        `${BASE_URL}catalogo/areas/${id}/incluir-legacy/`,
        { incluir },
        true,
      );
    },
    onSuccess: (_data, variables) => {
      queryClient.refetchQueries({ queryKey: ['ticket-areas'], type: 'active' });
      queryClient.refetchQueries({ queryKey: ['ticket-tipos'], type: 'active' });
      setAlert?.(
        true,
        `Toggle "Legacy Apertura" ${variables.incluir ? 'activado' : 'desactivado'} para el área.`,
        AlertSeverityEnum.success,
      );
    },
    onError: (error) => handleAxiosError(error),
  });
};

/**
 * Toggle independiente para el arbol de cierre (V1_20_0). Controla si
 * el area ve las categorias/subcategorias default cuando se cierra un
 * ticket de esa area. Es independiente del toggle "Legacy Apertura".
 */
export const useToggleAreaIncluirCierreLegacy = () => {
  const queryClient = useQueryClient();
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: async ({ id, incluir }: { id: string; incluir: boolean }) => {
      return patch<ITicketArea>(
        `${BASE_URL}catalogo/areas/${id}/incluir-cierre-legacy/`,
        { incluir },
        true,
      );
    },
    onSuccess: (_data, variables) => {
      queryClient.refetchQueries({ queryKey: ['ticket-areas'], type: 'active' });
      queryClient.refetchQueries({ queryKey: ['arbol-cierre'], type: 'active' });
      setAlert?.(
        true,
        `Toggle "Legacy Cierre" ${variables.incluir ? 'activado' : 'desactivado'} para el área.`,
        AlertSeverityEnum.success,
      );
    },
    onError: (error) => handleAxiosError(error),
  });
};
